/**
 * Records where an equipment was scanned.
 *
 * Passive, best-effort capture: it must never block, slow down or interrupt the
 * scan that triggered it, and it never surfaces anything to the user — not a
 * spinner, not an error. A user who refuses the location permission simply keeps
 * scanning as before.
 */

import type { RecordEquipmentLocationDto } from '@geartracker/dto';
import { EquipmentLocationSource } from '@geartracker/types';

import { useRecordEquipmentLocation } from 'src/api/equipment-location.api';
import { scanLocationLogger as log } from 'src/components/capacitor-utils/nfc-logger';
import { usePlatformFlags } from 'src/hooks/use-platform-flags';

import type { PermissionStatus, Position } from '@capacitor/geolocation';
import { Geolocation } from '@capacitor/geolocation';
import { useCallback } from 'react';

/**
 * Don't re-capture the same equipment within this window. Absorbs a tag being
 * scanned repeatedly, a web QR code held in front of the camera, and component
 * remounts on navigation.
 */
export const SCAN_LOCATION_THROTTLE_MS = 2 * 60 * 1000;

const POSITION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10_000,
  // Lets a multi-scan batch reuse one fix instead of waking the GPS per item.
  maximumAge: 30_000,
} as const;

/**
 * Module-level on purpose: the throttle has to outlive the components that
 * trigger a capture, since scanning navigates away from them.
 */
const capturedAtByEquipment = new Map<number, number>();

/**
 * Drop entries whose window has run out: they throttle nothing, and without
 * this the map would grow for every equipment scanned in a session that never
 * ends — a picking round can cover hundreds.
 */
const forgetExpiredClaims = (now: number): void => {
  for (const [equipmentId, capturedAt] of capturedAtByEquipment) {
    if (now - capturedAt >= SCAN_LOCATION_THROTTLE_MS) {
      capturedAtByEquipment.delete(equipmentId);
    }
  }
};

/** How many equipments are currently being throttled. For tests. */
export const throttledEquipmentCount = (): number => capturedAtByEquipment.size;

/** Claim the ids we're about to capture, so concurrent scans don't pile up. */
const claimEquipmentIds = (equipmentIds: number[], now: number): number[] => {
  forgetExpiredClaims(now);

  const claimed = equipmentIds.filter((equipmentId) => {
    const capturedAt = capturedAtByEquipment.get(equipmentId);
    return (
      capturedAt === undefined || now - capturedAt >= SCAN_LOCATION_THROTTLE_MS
    );
  });

  for (const equipmentId of claimed) {
    capturedAtByEquipment.set(equipmentId, now);
  }

  return claimed;
};

/** Release a failed claim, so the next scan of that equipment retries at once. */
const releaseEquipmentIds = (equipmentIds: number[]): void => {
  for (const equipmentId of equipmentIds) {
    capturedAtByEquipment.delete(equipmentId);
  }
};

const isGranted = (status: PermissionStatus): boolean =>
  status.location === 'granted' || status.coarseLocation === 'granted';

const isRefused = (status: PermissionStatus): boolean =>
  status.location === 'denied' && status.coarseLocation === 'denied';

/**
 * On the web there is nothing to ask: `requestPermissions` isn't implemented,
 * and the browser prompts on `getCurrentPosition` itself.
 */
const ensureLocationPermission = async (
  isNative: boolean,
): Promise<boolean> => {
  if (!isNative) {
    return true;
  }

  // Throws when the OS location services are switched off entirely.
  const status = await Geolocation.checkPermissions();

  if (isGranted(status)) {
    return true;
  }
  if (isRefused(status)) {
    log.debug('Location permission refused, skipping capture');
    return false;
  }

  return isGranted(await Geolocation.requestPermissions());
};

/** Map a device fix to the API payload, or null when it isn't usable. */
const toLocationPayload = ({
  coords,
}: Position): RecordEquipmentLocationDto | null => {
  const { latitude, longitude, accuracy } = coords;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    latitude,
    longitude,
    // The API only accepts a positive accuracy; some devices report 0 or null.
    ...(Number.isFinite(accuracy) && accuracy > 0 ? { accuracy } : {}),
    source: EquipmentLocationSource.SCAN,
  };
};

/**
 * Returns `recordScanLocation(equipmentIds)`, to call as soon as a scan has
 * resolved to one or more equipments. The promise never rejects and resolves
 * once the capture has been attempted — call sites can safely ignore it.
 */
export const useRecordScanLocation = () => {
  const { isNative, isDesktopWeb } = usePlatformFlags();
  const { mutate: recordEquipmentLocation } = useRecordEquipmentLocation();

  return useCallback(
    async (equipmentIds: number[]): Promise<void> => {
      // A desktop browser fix is derived from the IP address: it would say
      // "the equipment is somewhere in this city", which is worse than nothing.
      if (isDesktopWeb || equipmentIds.length === 0) {
        return;
      }

      const claimed = claimEquipmentIds(equipmentIds, Date.now());
      if (claimed.length === 0) {
        return;
      }

      try {
        if (!(await ensureLocationPermission(isNative))) {
          releaseEquipmentIds(claimed);
          return;
        }

        const position = await Geolocation.getCurrentPosition(POSITION_OPTIONS);
        const location = toLocationPayload(position);

        if (!location) {
          log.warn('Discarded a fix with unusable coordinates');
          releaseEquipmentIds(claimed);
          return;
        }

        for (const equipmentId of claimed) {
          recordEquipmentLocation({ equipmentId, location });
        }
        log.debug('Scan location recorded', { equipmentIds: claimed });
      } catch (error) {
        // Refused permission, disabled location services, GPS timeout: all
        // expected in the field and all silent by design.
        releaseEquipmentIds(claimed);
        log.debug('Scan location capture skipped', error);
      }
    },
    [isDesktopWeb, isNative, recordEquipmentLocation],
  );
};
